import type {
  BprFinding,
  BprProblemPriority,
  BprProblemScope,
  BprProblemStage,
  SalesforceBprCategory,
} from '@protoss/shared';
import { SALESFORCE_BPR_CATEGORY_DEFS } from '@protoss/shared';
import type { PillVariant } from '@protoss/ui';
import { Button, Pill } from '@protoss/ui';
import * as React from 'react';
import { bprApi } from '../services/bprApi';

function priorityVariant(p?: BprProblemPriority): PillVariant {
  switch (p) {
    case 'p1':
      return 'danger';
    case 'p2':
      return 'accent';
    case 'p3':
    default:
      return 'neutral';
  }
}

function priorityLabel(p?: BprProblemPriority): string {
  switch (p) {
    case 'p1':
      return 'P1 - Critical';
    case 'p2':
      return 'P2 - Important';
    case 'p3':
      return 'P3 - Secondary';
    default:
      return 'Unprioritized';
  }
}

function scopeVariant(s?: BprProblemScope): PillVariant {
  switch (s) {
    case 'in_scope':
      return 'success';
    case 'out_of_scope':
      return 'muted';
    case 'unclear':
    default:
      return 'accent';
  }
}

function scopeLabel(s?: BprProblemScope): string {
  switch (s) {
    case 'in_scope':
      return 'In Scope';
    case 'out_of_scope':
      return 'Out of Scope';
    case 'unclear':
      return 'Unclear Scope';
    default:
      return 'Scope Pending';
  }
}

function stageLabel(st?: BprProblemStage): string {
  switch (st) {
    case 'current_pain':
      return 'Current Pain';
    case 'planned':
      return 'Planned Phase';
    case 'nice_to_have':
      return 'Nice to Have';
    default:
      return 'Current State';
  }
}

function findingStatusVariant(status: BprFinding['status']): PillVariant {
  switch (status) {
    case 'confirmed':
      return 'success';
    case 'dismissed':
      return 'muted';
    case 'draft':
    default:
      return 'neutral';
  }
}

interface BprSimpleProblemsSectionProps {
  engagementUuid: string;
  findings: BprFinding[];
  onChanged: () => void;
}

export function BprSimpleProblemsSection({
  engagementUuid,
  findings,
  onChanged,
}: BprSimpleProblemsSectionProps) {
  const [updatingUuid, setUpdatingUuid] = React.useState<string | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [filterScope, setFilterScope] = React.useState<'all' | 'in_scope' | 'draft' | 'confirmed' | 'dismissed'>('all');

  // Sort findings: P1 first, then P2, then P3, then stableRank/createdAt
  const sortedFindings = React.useMemo(() => {
    const priorityWeight: Record<string, number> = { p1: 1, p2: 2, p3: 3 };
    return [...findings].sort((a, b) => {
      const pA = priorityWeight[a.problemMeta?.priority ?? 'p3'] ?? 99;
      const pB = priorityWeight[b.problemMeta?.priority ?? 'p3'] ?? 99;
      if (pA !== pB) return pA - pB;
      const rankA = a.problemMeta?.stableRank ?? 999;
      const rankB = b.problemMeta?.stableRank ?? 999;
      if (rankA !== rankB) return rankA - rankB;
      return a.createdAt.localeCompare(b.createdAt);
    });
  }, [findings]);

  const filteredFindings = React.useMemo(() => {
    if (filterScope === 'all') return sortedFindings;
    if (filterScope === 'in_scope') {
      return sortedFindings.filter((f) => f.problemMeta?.scope === 'in_scope');
    }
    return sortedFindings.filter((f) => f.status === filterScope);
  }, [sortedFindings, filterScope]);

  const counts = React.useMemo(() => {
    let confirmed = 0;
    let dismissed = 0;
    let draft = 0;
    let quarantined = 0;
    for (const f of findings) {
      if (f.status === 'confirmed') confirmed++;
      else if (f.status === 'dismissed') dismissed++;
      else draft++;

      const evidence = f.problemMeta?.evidence ?? [];
      if (evidence.length === 0) quarantined++;
    }
    return { total: findings.length, confirmed, dismissed, draft, quarantined };
  }, [findings]);

  const handleUpdateStatus = async (
    findingUuid: string,
    nextStatus: 'draft' | 'confirmed' | 'dismissed'
  ) => {
    if (updatingUuid) return;
    setUpdatingUuid(findingUuid);
    setError(null);
    try {
      await bprApi.updateFinding(engagementUuid, findingUuid, { status: nextStatus });
      onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update finding status.');
    } finally {
      setUpdatingUuid(null);
    }
  };

  return (
    <div className="bpr-simple-problems">
      <div className="bpr-simple-problems__header">
        <div className="bpr-simple-problems__titles">
          <h3 className="bpr-section__heading">Detected Problems ({findings.length})</h3>
          <p className="bpr-simple-problems__subtitle">
            Grounded findings extracted from discovery transcripts with verbatim quote verification.
            Review and confirm problems to propose solution options.
          </p>
        </div>
        <div className="bpr-simple-problems__filters">
          <Button
            type="button"
            variant={filterScope === 'all' ? 'primary' : 'outline'}
            size="sm"
            onClick={() => setFilterScope('all')}
          >
            All ({counts.total})
          </Button>
          <Button
            type="button"
            variant={filterScope === 'draft' ? 'primary' : 'outline'}
            size="sm"
            onClick={() => setFilterScope('draft')}
          >
            Draft ({counts.draft})
          </Button>
          <Button
            type="button"
            variant={filterScope === 'confirmed' ? 'primary' : 'outline'}
            size="sm"
            onClick={() => setFilterScope('confirmed')}
          >
            Confirmed ({counts.confirmed})
          </Button>
          <Button
            type="button"
            variant={filterScope === 'dismissed' ? 'primary' : 'outline'}
            size="sm"
            onClick={() => setFilterScope('dismissed')}
          >
            Dismissed ({counts.dismissed})
          </Button>
        </div>
      </div>

      {error ? <p className="bpr-dialog__error">{error}</p> : null}

      {filteredFindings.length === 0 ? (
        <div className="bpr-section__empty">
          <p>No problems found matching this filter.</p>
        </div>
      ) : (
        <ul className="bpr-simple-problems__list">
          {filteredFindings.map((finding) => {
            const meta = finding.problemMeta;
            const categoryDef =
              SALESFORCE_BPR_CATEGORY_DEFS[finding.functionalAreaKey as SalesforceBprCategory];
            const categoryTitle = categoryDef?.title ?? finding.functionalAreaKey;
            const evidenceList = meta?.evidence ?? [];
            const isQuarantined = evidenceList.length === 0;
            const isUpdating = updatingUuid === finding.uuid;

            return (
              <li
                key={finding.uuid}
                className={`bpr-simple-problems__card${
                  finding.status === 'dismissed' ? ' bpr-simple-problems__card--dismissed' : ''
                }`}
              >
                <div className="bpr-simple-problems__card-header">
                  <div className="bpr-simple-problems__badges">
                    <Pill variant={priorityVariant(meta?.priority)}>
                      {priorityLabel(meta?.priority)}
                    </Pill>
                    <Pill variant="neutral">{categoryTitle}</Pill>
                    <Pill variant={scopeVariant(meta?.scope)}>{scopeLabel(meta?.scope)}</Pill>
                    <Pill variant="ghost">{stageLabel(meta?.stage)}</Pill>
                    <Pill variant={findingStatusVariant(finding.status)}>{finding.status}</Pill>
                  </div>
                  <div className="bpr-simple-problems__actions">
                    {finding.status !== 'confirmed' ? (
                      <Button
                        type="button"
                        size="sm"
                        disabled={isUpdating}
                        onClick={() => void handleUpdateStatus(finding.uuid, 'confirmed')}
                      >
                        {isUpdating ? 'Updating…' : 'Confirm'}
                      </Button>
                    ) : null}
                    {finding.status !== 'dismissed' ? (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled={isUpdating}
                        onClick={() => void handleUpdateStatus(finding.uuid, 'dismissed')}
                      >
                        Dismiss
                      </Button>
                    ) : null}
                    {finding.status === 'dismissed' ? (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled={isUpdating}
                        onClick={() => void handleUpdateStatus(finding.uuid, 'draft')}
                      >
                        Restore
                      </Button>
                    ) : null}
                  </div>
                </div>

                <div className="bpr-simple-problems__card-body">
                  <h4 className="bpr-simple-problems__summary">
                    {finding.currentStateSummary ?? 'No summary provided.'}
                  </h4>

                  {meta?.priorityReason ? (
                    <p className="bpr-simple-problems__reason">
                      <strong>Priority context:</strong> {meta.priorityReason}
                    </p>
                  ) : null}

                  {meta?.scopeReason ? (
                    <p className="bpr-simple-problems__reason">
                      <strong>Scope context:</strong> {meta.scopeReason}
                    </p>
                  ) : null}

                  {meta?.objectTags && meta.objectTags.length > 0 ? (
                    <div className="bpr-simple-problems__tags">
                      <span className="bpr-simple-problems__tags-label">Related Objects:</span>
                      {meta.objectTags.map((tag) => (
                        <span key={tag} className="bpr-simple-problems__tag">
                          {tag}
                        </span>
                      ))}
                    </div>
                  ) : null}

                  {isQuarantined ? (
                    <div className="bpr-simple-problems__quarantine-banner">
                      <span className="bpr-simple-problems__quarantine-icon">⚠️</span>
                      <div>
                        <strong>Quarantined finding:</strong> Verbatim transcript quote could not be
                        verified against source session notes (Trust Program B3). Requires consultant review before proceeding.
                      </div>
                    </div>
                  ) : (
                    <div className="bpr-simple-problems__evidence-section">
                      <span className="bpr-simple-problems__evidence-label">Grounded Quote:</span>
                      {evidenceList.map((ev, idx) => (
                        <blockquote key={idx} className="bpr-simple-problems__quote">
                          <p className="bpr-simple-problems__quote-text">“{ev.quote}”</p>
                          <footer className="bpr-simple-problems__quote-speaker">
                            — {ev.speaker ? ev.speaker : 'Discovery Transcript'}
                          </footer>
                        </blockquote>
                      ))}
                    </div>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
